import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import HowManyPercentDone from "../../components/HowManyPercentDone/HowManyPercentDone";
import styles from "./Calendar.module.scss";
import Filters from "../../components/Filters/Filters";
import Habit from "../../components/Habit/Habit";
import Moods from "../../components/Moods/Moods";
import { useDispatch, useSelector } from "react-redux";
import { fetchHabits } from "../../store/habitsSlice";
import { getLocalDateString } from "../../utils/date";
import { fetchCategories } from "../../store/categoriesSlice";
import { moodIcons } from '../../components/Moods/Moods'
import { fetchMoodsByMonth } from "../../store/moodsSlice";
import { getDaysInMonth, getFilteredHabits, getHabitCountsByDate, getHabitCountsForDate } from "../../utils/calendarUtils";
import { getFirstDayOfMonth } from "../../utils/calendarUtils";
import { getMonthName } from "../../utils/calendarUtils";
import MonthNavigation from "../../components/MonthNavigation/MonthNavigation";

export const Calendar = () => {
    const dispatch = useDispatch();

    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [filter, setFilter] = useState("all");
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 543);


    const moodsByDate = useSelector((state) => state.moods.moodsByDate);
    const userId = useSelector((state) => state.auth.user?.id);
    const habits = useSelector((state) => state.habits.habits);


    const dateStr = selectedDate ? getLocalDateString(selectedDate) : "";
    const selectedMood = moodsByDate?.[dateStr] || null;

    const fullWeekdays = ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"];
    const shortWeekdays = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

    const [screen, setScreen] = useState({
        isMobile: window.innerWidth <= 543,
        isMobileHabit: window.innerWidth <= 1245
    });
    
    useLayoutEffect(() => {
        setIsMobile(window.innerWidth <= 543);
    }, []);

    // useEffect(() => {
    //     const handleResize = () => setIsMobile(window.innerWidth <= 420);
    //     window.addEventListener("resize", handleResize);
    //     return () => window.removeEventListener("resize", handleResize);
    // }, []);

    useEffect(() => {
        const handleResize = () => {
            setScreen({
                isMobile: window.innerWidth <= 543,
                isMobileHabit: window.innerWidth <= 1245
            });
        };
        window.addEventListener("resize", handleResize);
        // Инициализация при первом рендере
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const handleDayClick = (day) => {
        if (!day) return;
        setSelectedDate(new Date(currentYear, currentMonth, day));
    };

    useEffect(() => {
        setSelectedDate(new Date());
    }, []);

    useEffect(() => {
        if (userId && habits.length === 0) {
            dispatch(fetchCategories(userId));
            dispatch(fetchHabits(userId));
        }
    }, [userId, habits.length, dispatch]);

    useEffect(() => {
        if (userId) {
            dispatch(fetchMoodsByMonth({ userId, year: currentYear, month: currentMonth }));
        }
    }, [userId, currentYear, currentMonth, dispatch]);



    const changeMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() + direction);

        if (newDate.getMonth() < currentDate.getMonth() - 3 || newDate.getMonth() > currentDate.getMonth() + 3) {
            return;
        }

        setCurrentDate(newDate);

        const today = new Date();
        if (
            newDate.getMonth() === today.getMonth() &&
            newDate.getFullYear() === today.getFullYear()
        ) {
            setSelectedDate(today);
        } else {
            setSelectedDate(null);
        }
    };

    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDayOfMonth = getFirstDayOfMonth(currentMonth, currentYear);

    const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const emptyCells = Array.from({ length: firstDayOfMonth }, (_, i) => null);

    const calendarDays = [...emptyCells, ...daysArray];


    const { active: activeCount, completed: completedCount, withDate: withDateCount } = getHabitCountsForDate(habits, selectedDate);

    const filteredHabits = getFilteredHabits(habits, selectedDate, filter);


    const habitCountsByDate = useMemo(
        () => getHabitCountsByDate(habits, currentMonth, currentYear, daysInMonth),
        [habits, currentMonth, currentYear, daysInMonth]
    );

    const activeHabitsForDate = habits.filter(habit => {
        const sel = new Date(selectedDate).setHours(0, 0, 0, 0);
        const start = new Date(habit.created_at).setHours(0, 0, 0, 0);
        const end = habit.deadline 
            ? new Date(habit.deadline).setHours(0, 0, 0, 0)
            : Infinity;

        return sel >= start && sel <= end;
    });

    

    return (
        <div className={styles.container}>
            <div className={styles.heading_block}>
                <p className={styles.heading}>Календарь</p>
            </div>

            <div className={styles.container_calendar}>
                <div className={styles.calendar_block}>
                    <MonthNavigation
                        currentDate={currentDate}
                        currentMonth={currentMonth}
                        currentYear={currentYear}
                        onChangeMonth={changeMonth}
                        getMonthName={getMonthName}
                        minMonth={new Date().getMonth() - 3}
                        maxMonth={new Date().getMonth() + 3}
                    />

                    <div className={styles.calendar}>
                        <div className={styles.weekdays}>
                            {(screen.isMobile ? shortWeekdays : fullWeekdays).map((day, index) => (
                                <div 
                                    key={index}
                                    data-full={fullWeekdays[index]}
                                    data-short={shortWeekdays[index]}
                                >
                                </div>
                            ))}
                        </div>

                        <div className={styles.days}>
                            {calendarDays.map((day, index) => {
                                let isSelected = false;
                                if (selectedDate && day) {
                                    isSelected =
                                        selectedDate.getDate() === day &&
                                        selectedDate.getMonth() === currentMonth &&
                                        selectedDate.getFullYear() === currentYear;
                                }
                                return (
                                    <div
                                        key={index}
                                        className={`${styles.day} ${isSelected ? styles.selected : ""}`}
                                        onClick={() => handleDayClick(day)}
                                    >
                                        <p className={styles.one_day}>{day || ""}</p>
                                        <div className={styles.one_mood}>
                                            {day && (() => {
                                                const dateKey = getLocalDateString(new Date(currentYear, currentMonth, day));
                                                const moodValue = moodsByDate?.[dateKey];
                                                const MoodIconSrc = moodValue ? moodIcons[moodValue - 1] : null;

                                                return (
                                                    <>
                                                        {MoodIconSrc && (
                                                            <div className={styles.calendar_mood_icon}>
                                                                <img
                                                                    src={MoodIconSrc}
                                                                    alt="mood"
                                                                    width={24}
                                                                    height={24}
                                                                />
                                                            </div>
                                                        )}   

                                                        {habitCountsByDate[dateKey] > 0 && (
                                                            <div className={styles.habit_count}>
                                                                {habitCountsByDate[dateKey]}
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>

                    <div>
                        <HowManyPercentDone />
                    </div>

                </div>

                <div className={styles.right_block}>
                    <div className={styles.date_block}>
                        <div className={styles.info_block}>
                            <p className={styles.right_date}>
                                {selectedDate ? selectedDate.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" }) : "Выберите дату"}
                            </p>                        
                            <p className={styles.your_habits}>Твои привычки на сегодня 
                                <span className={styles.habit_count_circle}>{activeHabitsForDate.length}</span>
                            </p>
                        </div>
                        
                        <div className={styles.block_filters}>
                            <Filters
                                activeCount={activeCount}
                                completedCount={completedCount}
                                withDateCount={withDateCount}
                                setFilter={setFilter}
                                currentFilter={filter}
                            />

                        </div>
                        
                        <div className={styles.habits}>
                            {filteredHabits.length > 0 ? (
                                filteredHabits.map((habit) => (
                                    <Habit 
                                        key={habit.id} 
                                        habit={habit} 
                                        style={{ margin: "10px 0px" }}
                                        // customWidth={screen.isMobileHabit ? "300px" : "380px"}
                                        selectedDate={getLocalDateString(selectedDate)}
                                        className={!isMobile ? styles.habit_desktop : ""}
                                    />
                                ))
                            ) : (
                                <p>На эту дату привычек нет.</p>
                            )}

                        </div>

                        <div className={styles.moods_block}>
                            <p className={styles.mood_text}>Какое у вас сегодня настроение?</p>
                            <Moods 
                                style={{ width: "35px", margin: "10px", gap: "10px", }}
                                selectedMood={selectedMood} 
                                selectedDate={selectedDate} 
                                userId={userId}
                            />

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
